import type { Artifact } from '@/enterprise/data/useArtifacts';

const FUNDBOX_NAV_DESTINATION_KEY = 'TERM_LOANS_BY_FUNDBOX';
const NAV_BAR_PLACEMENT = 'nav-bar';

interface NavigationArtifactContent {
  text: string;
  icon: { key: string };
  destination: { key: string };
  target: string;
}

export type FundboxNavigationArtifact = Artifact & { content: NavigationArtifactContent };

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

/** NAVIGATION + nav-bar + Fundbox destination with renderable label, icon, and relative target. */
const isFundboxNavigationArtifact = (artifact: Artifact): artifact is FundboxNavigationArtifact => {
  if (artifact.artifactType !== 'NAVIGATION' || !artifact.placements?.includes(NAV_BAR_PLACEMENT)) {
    return false;
  }

  const { content } = artifact;
  if (
    content === null ||
    content === undefined ||
    !('text' in content) ||
    !('destination' in content) ||
    !('target' in content) ||
    !('icon' in content)
  ) {
    return false;
  }

  if (!isNonEmptyString(content.text)) {
    return false;
  }

  if (
    typeof content.destination !== 'object' ||
    content.destination === null ||
    !isNonEmptyString(content.destination.key) ||
    content.destination.key !== FUNDBOX_NAV_DESTINATION_KEY
  ) {
    return false;
  }

  if (!isNonEmptyString(content.target) || content.target.startsWith('/')) {
    return false;
  }

  if (typeof content.icon !== 'object' || content.icon === null || !isNonEmptyString(content.icon.key)) {
    return false;
  }

  return true;
};

/** Returns the highest-priority Fundbox nav-bar NAVIGATION artifact, if any.
 * There should only be one Fundbox nav-bar NAVIGATION artifact, priority check
 * is just a super just-in-case fallback.
 */
export const selectFundboxNavigationArtifact = (artifacts: Artifact[]): FundboxNavigationArtifact | undefined => {
  let selected: FundboxNavigationArtifact | undefined;

  for (const artifact of artifacts) {
    if (!isFundboxNavigationArtifact(artifact)) {
      continue;
    }

    if (!selected || artifact.priority > selected.priority) {
      selected = artifact;
    }
  }

  return selected;
};
